import { ButtonBase, Chip, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import {
	ClientPortfolioInvestmentTypeEnum,
	type CurrencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import {
	type InstructionTypeLabels,
	useInstructionTypeOptions,
} from "web_myplann/Portfolio/Forms/ToolsShared/useInstructionOptions";
import { getPortfolioCurrencySign } from "web_myplann/Portfolio/Helpers/getPortfolioCurrencySign";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { numberFormatter } from "web_myplann/Shared/Utils/numberFormatters";

type Props = {
	investmentType: ClientPortfolioInvestmentTypeEnum | null;
	amount: number | null;
	currency: CurrencyEnum;
	date: Date | null;
	typeLabels?: InstructionTypeLabels;
	isLast: boolean;
	onClick: () => void;
};

export const InstructionRow: FC<Props> = ({
	investmentType,
	amount,
	currency,
	date,
	typeLabels,
	isLast,
	onClick,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const typeOptions = useInstructionTypeOptions(typeLabels);

	const typeLabel =
		typeOptions.find((option) => option.value === investmentType)?.label ?? "";

	return (
		<Stack
			component={ButtonBase}
			direction="row"
			alignItems="center"
			gap={theme.spacingTokens.sm}
			px={theme.spacingTokens.lg}
			py={theme.spacingTokens.md}
			width="100%"
			textAlign="left"
			onClick={onClick}
			sx={{
				borderBottom: isLast
					? "none"
					: theme.colors.border.solid(theme.palette.grey[100]),
			}}
		>
			{typeLabel && (
				<Chip
					variant="badge"
					color={
						investmentType === ClientPortfolioInvestmentTypeEnum.Buy
							? "success"
							: "error"
					}
					label={typeLabel}
				/>
			)}
			<Typography
				variant="caption"
				color={theme.palette.grey[500]}
				flexGrow={1}
				minWidth={0}
				tabular
				noWrap
			>
				{dateFormatter(date)}
			</Typography>
			{amount !== null && (
				<Typography variant="bodyMedium" flexShrink={0} tabular>
					{`${numberFormatter.format(amount)} ${getPortfolioCurrencySign(t, currency)}`}
				</Typography>
			)}
			<ChevronRight
				size={theme.iconSize.sm}
				color={theme.palette.grey[400]}
				style={{ flexShrink: 0 }}
			/>
		</Stack>
	);
};
